import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import TimeIcon from '../DetailComponents/Icons/Time';
import SupporterIcon from '../DetailComponents/Icons/SupporterIcon';
import ProgressBar from '../helpers/ProgressBar';
import { isMobile, isTablet } from '../../../../../utils';
import { getTicketProgressInfo } from '../utils/TicketPickCalculation';

const StickyProjectBarWrap = styled.div`
  position: fixed;
  top: 0px;
  left: 50%;
  transform: translateX(-50%)
    translateY(${({ $visible }) => ($visible ? '0' : '-16px')});
  width: 100%;
  justify-content: center;
  align-content: center;
  align-items: center;

  z-index: 999;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.22s ease, transform 0.22s ease;
  height: 85px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0px 12px 16px -4px rgba(10, 13, 18, 0.08),
    0px 4px 6px -2px rgba(10, 13, 18, 0.03);
  backdrop-filter: blur(8px);
`;

const StickyProjectBar = styled.div`
  display: flex;
  margin: 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: clamp(16px, 4vw, 63px);
  min-height: 48px;
  height: 48px;
  padding: 10px 16px;
  width: 100%;
  max-width: 1280px;
  min-width: 0;

  @media (max-width: 900px) {
    gap: 12px;
    padding: 12px;
  }

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Left = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
    gap: 9px;
  }
`;

const Title = styled.div`
  min-width: 0;
  max-width: 379px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 24px;
  
  /* Text md/Semibold */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;

  color: #535862;
`;

const Subtitle = styled.div`
  min-width: 0;
  max-width: 364px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 24px;

  /* Text md/Regular */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  color: #535862;
`;


const MetaGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  flex-wrap: wrap;
`;

const MetaItems = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 9px;
  width: 100%;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #656565;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  white-space: ${({ $allowWrap }) => ($allowWrap ? 'normal' : 'nowrap')};

  svg {
    flex: 0 0 auto;
  }
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  width: min(331px, 28vw);
  min-width: 220px;
  gap: 9px;
  flex: 1;

  @media (max-width: 900px) {
    min-width: 0;
    width: 100%;
  }
`
const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 0 0 auto;

  @media (max-width: 720px) {
    width: 100%;
  }
`;

const SecondaryButton = styled.button`
  height: 44px;
  padding: 0px 16px;
  border-radius: 8px;
  border: 1px solid #d5d7da;
  background: #ffffff;
  color: #414651;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);

  &:hover {
    background: #f8f9fb;
  }

  @media (max-width: 720px) {
    flex: 1;
  }
`;

const PrimaryButton = styled.button`
  height: 44px;
  padding: 0px 16px;
  border-radius: 8px;
  border: 1px solid #9dbc60;
  background: #9dbc60;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: inset 0px -2px 0px rgba(0, 0, 0, 0.08);
  transition: background 0.22s ease;
  &:hover {
    background: #3f621a;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    background: #9dbc60;
  }

  @media (max-width: 720px) {
    flex: 1;
  }
`;

const DEFAULT_TRIGGER = 100;

const ProjectStickyHeader = ({
  title,
  subTitle,
  supportersText,
  daysLeftText,
  onShareClick,
  onSupportClick,
  triggerOffset = DEFAULT_TRIGGER,
  project,
  projectDonations,
  rewardsUpper,
  isEvent
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > triggerOffset);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [triggerOffset]);

  const percentage = Math.floor(((projectDonations || 0) / project.goal) * 100);
  const percentageForTotalGoal = Math.floor(
    ((projectDonations || 0) / project?.final_goal) * 100
  );

  let progress = (percentage >= 100 ? percentageForTotalGoal : percentage) || 0;

  const ticketData = getTicketProgressInfo({
    project,
    tickets: rewardsUpper,
  });

  if (project.is_ticketable) {
    progress = ticketData.percentage ?? 0;
  }
  const viewMode = window.location?.pathname.startsWith('/view');

  return (
    <StickyProjectBarWrap $visible={isMobile() || isTablet() ? false : visible}>
      <StickyProjectBar>
        <Left>
          <Title title={title}>{title}</Title>
          <Subtitle title={subTitle}>{subTitle}</Subtitle>
        </Left>

        <Center>
          <MetaGroup>
            {!project.is_patreon && <ProgressBar percentage={progress} />}
            <MetaItems>
              <MetaItem>
                <SupporterIcon size={14} />
                <span>
                  {supportersText} {isEvent ? 'Besucher' : 'Unterstützer'}
                </span>
              </MetaItem>

              <MetaItem $allowWrap={isEvent && ticketData.isEnded}>
                <TimeIcon size={14} />
                <span>
                  {isEvent
                    ? ticketData.label
                    : `${daysLeftText} Tage noch`}
                </span>
              </MetaItem>
            </MetaItems>
          </MetaGroup>
        </Center>

        <Right>
          <SecondaryButton type="button" onClick={viewMode ? undefined : onShareClick}>
            {isEvent ? 'Event' : 'Projekt'} teilen
          </SecondaryButton>

          {(!isEvent || !ticketData.isEnded) && (
            <PrimaryButton
              type="button"
              disabled={viewMode}
              onClick={viewMode ? undefined : onSupportClick}
            >
              {isEvent ? 'Ticket buchen' : 'Jetzt unterstützen'}
            </PrimaryButton>
          )}
        </Right>
      </StickyProjectBar>
    </StickyProjectBarWrap>
  );
};

export default ProjectStickyHeader;
